/**
 * Feedback API Client
 *
 * Enterprise-grade feedback + roadmap system API
 */

import { apiClient } from './client'

// === Types ===

export interface FeedbackTag {
  id: string
  name: string
  description: string | null
  color: string
  weight: number
  category: string | null
}

export interface FeedbackRequest {
  id: string
  tenant_id: string
  created_by: string
  title: string
  problem_statement: string
  desired_outcome: string | null
  workflow_stage: string | null
  urgency: 'low' | 'medium' | 'high'
  visibility: 'org_only' | 'council' | 'internal'
  status: FeedbackStatus
  status_reason: string | null
  vote_count: number
  follower_count: number
  comment_count: number
  is_duplicate: boolean
  canonical_request_id: string | null
  tags: FeedbackTag[]
  created_at: string
  updated_at: string
  shipped_at: string | null
  // Internal fields (admin only)
  deal_blocker?: boolean
  revenue_influence?: number
  risk_reduction?: number
  priority_score?: number
  weighted_votes?: number
  is_moderated?: boolean
  // User-specific
  has_voted?: boolean
  is_following?: boolean
  moderation_warnings?: string[]
}

export type FeedbackStatus =
  | 'submitted'
  | 'triaging'
  | 'planned'
  | 'in_progress'
  | 'beta'
  | 'shipped'
  | 'wont_do'
  | 'duplicate'

export interface FeedbackRequestCreate {
  title: string
  problem_statement: string
  desired_outcome?: string
  workflow_stage?: string
  urgency?: 'low' | 'medium' | 'high'
  tag_ids?: string[]
  deal_blocker?: boolean
}

export interface FeedbackRequestUpdate {
  title?: string
  problem_statement?: string
  desired_outcome?: string
  workflow_stage?: string
  urgency?: string
  tag_ids?: string[]
}

export interface FeedbackAdminUpdate {
  status?: FeedbackStatus
  status_reason?: string
  visibility?: 'org_only' | 'council' | 'internal'
  revenue_influence?: number
  risk_reduction?: number
  canonical_request_id?: string
}

export interface FeedbackVote {
  id: string
  request_id: string
  user_id: string
  weight: number
  is_following: boolean
  created_at: string
}

export interface FeedbackComment {
  id: string
  request_id: string
  user_id: string
  user_name: string | null
  body: string
  is_internal: boolean
  created_at: string
}

export interface FeedbackStatusUpdate {
  id: string
  request_id: string
  status_before: string
  status_after: string
  message: string | null
  changed_by: string
  created_at: string
}

export interface FeedbackNotification {
  id: string
  request_id: string | null
  notification_type: string
  title: string
  body: string | null
  is_read: boolean
  created_at: string
}

export interface SimilarRequest {
  id: string
  title: string
  problem_statement: string
  status: string
  vote_count: number
  similarity_score: number
}

export interface RoadmapItem {
  id: string
  title: string
  description: string | null
  category: 'now' | 'next' | 'later' | 'shipped'
  sort_order: number
  shipped_at: string | null
  created_at: string
  // Admin only
  linked_request_ids?: string[]
  defensibility_notes?: string
  impact_on_audit?: string
  new_settings?: string
  migration_notes?: string
  created_by?: string
}

export interface RoadmapItemCreate {
  title: string
  description?: string
  category: 'now' | 'next' | 'later' | 'shipped'
  sort_order?: number
  linked_request_ids?: string[]
  defensibility_notes?: string
  impact_on_audit?: string
  new_settings?: string
  migration_notes?: string
}

export interface FeedbackListResponse {
  items: FeedbackRequest[]
  total: number
  page: number
  page_size: number
  has_more: boolean
}

export interface NotificationListResponse {
  items: FeedbackNotification[]
  total: number
  unread_count: number
}

export interface RoadmapResponse {
  now: RoadmapItem[]
  next: RoadmapItem[]
  later: RoadmapItem[]
  shipped: RoadmapItem[]
}

export interface FeedbackStats {
  total_requests: number
  requests_by_status: Record<string, number>
  requests_by_urgency: Record<string, number>
  top_voted: FeedbackRequest[]
  recent_updates: FeedbackStatusUpdate[]
}

// === API Client ===

const ENDPOINT = '/feedback'

export const feedbackApi = {
  // === Requests ===

  async createRequest(
    data: FeedbackRequestCreate
  ): Promise<FeedbackRequest> {
    const { data: response } = await apiClient.post<FeedbackRequest>(
      `${ENDPOINT}/requests`,
      data
    )
    return response
  },

  async listRequests(params?: {
    status?: FeedbackStatus
    page?: number
    page_size?: number
    sort_by?: string
    sort_desc?: boolean
  }): Promise<FeedbackListResponse> {
    const { data } = await apiClient.get<FeedbackListResponse>(
      `${ENDPOINT}/requests`,
      { params }
    )
    return data
  },

  async getRequest(requestId: string): Promise<FeedbackRequest> {
    const { data } = await apiClient.get<FeedbackRequest>(
      `${ENDPOINT}/requests/${requestId}`
    )
    return data
  },

  async updateRequest(
    requestId: string,
    data: FeedbackRequestUpdate
  ): Promise<FeedbackRequest> {
    const { data: response } = await apiClient.patch<FeedbackRequest>(
      `${ENDPOINT}/requests/${requestId}`,
      data
    )
    return response
  },

  async findSimilar(
    title: string,
    problem_statement: string
  ): Promise<SimilarRequest[]> {
    const { data } = await apiClient.post<SimilarRequest[]>(
      `${ENDPOINT}/requests/similar`,
      null,
      { params: { title, problem_statement } }
    )
    return data
  },

  // === Votes ===

  async vote(
    requestId: string,
    isFollowing: boolean = true
  ): Promise<FeedbackVote> {
    const { data } = await apiClient.post<FeedbackVote>(
      `${ENDPOINT}/requests/${requestId}/vote`,
      { is_following: isFollowing }
    )
    return data
  },

  async unvote(requestId: string): Promise<void> {
    await apiClient.delete(`${ENDPOINT}/requests/${requestId}/vote`)
  },

  async updateFollow(
    requestId: string,
    isFollowing: boolean
  ): Promise<{ is_following: boolean }> {
    const { data } = await apiClient.patch<{ is_following: boolean }>(
      `${ENDPOINT}/requests/${requestId}/follow`,
      null,
      { params: { is_following: isFollowing } }
    )
    return data
  },

  // === Comments ===

  async addComment(
    requestId: string,
    body: string,
    isInternal: boolean = false
  ): Promise<FeedbackComment> {
    const { data } = await apiClient.post<FeedbackComment>(
      `${ENDPOINT}/requests/${requestId}/comments`,
      { body, is_internal: isInternal }
    )
    return data
  },

  async listComments(requestId: string): Promise<FeedbackComment[]> {
    const { data } = await apiClient.get<FeedbackComment[]>(
      `${ENDPOINT}/requests/${requestId}/comments`
    )
    return data
  },

  // === Status History ===

  async getStatusHistory(requestId: string): Promise<FeedbackStatusUpdate[]> {
    const { data } = await apiClient.get<FeedbackStatusUpdate[]>(
      `${ENDPOINT}/requests/${requestId}/status-history`
    )
    return data
  },

  // === Tags ===

  async listTags(category?: string): Promise<FeedbackTag[]> {
    const { data } = await apiClient.get<FeedbackTag[]>(
      `${ENDPOINT}/tags`,
      { params: category ? { category } : undefined }
    )
    return data
  },

  // === Notifications ===

  async getNotifications(params?: {
    unread_only?: boolean
    page?: number
    page_size?: number
  }): Promise<NotificationListResponse> {
    const { data } = await apiClient.get<NotificationListResponse>(
      `${ENDPOINT}/notifications`,
      { params }
    )
    return data
  },

  async markNotificationRead(notificationId: string): Promise<void> {
    await apiClient.post(`${ENDPOINT}/notifications/${notificationId}/read`)
  },

  async markAllNotificationsRead(): Promise<{ marked_read: number }> {
    const { data } = await apiClient.post<{ marked_read: number }>(
      `${ENDPOINT}/notifications/read-all`
    )
    return data
  },

  // === Roadmap ===

  async getRoadmap(): Promise<RoadmapResponse> {
    const { data } = await apiClient.get<RoadmapResponse>(`${ENDPOINT}/roadmap`)
    return data
  },

  // === Stats ===

  async getStats(): Promise<FeedbackStats> {
    const { data } = await apiClient.get<FeedbackStats>(`${ENDPOINT}/stats`)
    return data
  },
}

// === Admin API ===

const ADMIN_ENDPOINT = '/admin/feedback'

export const feedbackAdminApi = {
  async adminUpdateRequest(
    requestId: string,
    data: FeedbackAdminUpdate
  ): Promise<FeedbackRequest> {
    const { data: response } = await apiClient.patch<FeedbackRequest>(
      `${ADMIN_ENDPOINT}/requests/${requestId}`,
      data
    )
    return response
  },

  async updateStatus(
    requestId: string,
    status: FeedbackStatus,
    message?: string,
    statusReason?: string
  ): Promise<FeedbackStatusUpdate> {
    const { data } = await apiClient.post<FeedbackStatusUpdate>(
      `${ADMIN_ENDPOINT}/requests/${requestId}/status`,
      { status, message, status_reason: statusReason }
    )
    return data
  },

  async mergeRequests(
    requestId: string,
    canonicalId: string
  ): Promise<{ merged: boolean; canonical_id: string }> {
    const { data } = await apiClient.post<{ merged: boolean; canonical_id: string }>(
      `${ADMIN_ENDPOINT}/requests/${requestId}/merge`,
      null,
      { params: { canonical_id: canonicalId } }
    )
    return data
  },

  async listAllRequests(params?: {
    status?: FeedbackStatus
    tenant_id?: string
    sort_by?: string
    sort_desc?: boolean
    page?: number
    page_size?: number
  }): Promise<FeedbackListResponse> {
    const { data } = await apiClient.get<FeedbackListResponse>(
      `${ADMIN_ENDPOINT}/requests`,
      { params }
    )
    return data
  },

  async getAdminRoadmap(): Promise<RoadmapResponse> {
    const { data } = await apiClient.get<RoadmapResponse>(`${ADMIN_ENDPOINT}/roadmap`)
    return data
  },

  async createRoadmapItem(data: RoadmapItemCreate): Promise<RoadmapItem> {
    const { data: response } = await apiClient.post<RoadmapItem>(
      `${ADMIN_ENDPOINT}/roadmap`,
      data
    )
    return response
  },

  async updateRoadmapItem(
    itemId: string,
    data: Partial<RoadmapItemCreate>
  ): Promise<RoadmapItem> {
    const { data: response } = await apiClient.patch<RoadmapItem>(
      `${ADMIN_ENDPOINT}/roadmap/${itemId}`,
      data
    )
    return response
  },
}

// === Status Display Helpers ===

export const statusConfig: Record<
  FeedbackStatus,
  { label: string; color: string; bgColor: string }
> = {
  submitted: { label: 'Submitted', color: 'text-muted-foreground', bgColor: 'bg-muted/50' },
  triaging: { label: 'Triaging', color: 'text-info', bgColor: 'bg-info/15' },
  planned: { label: 'Planned', color: 'text-primary', bgColor: 'bg-primary/10' },
  in_progress: { label: 'In Progress', color: 'text-warning', bgColor: 'bg-warning/20' },
  beta: { label: 'Beta', color: 'text-info', bgColor: 'bg-info/20' },
  shipped: { label: 'Shipped', color: 'text-success', bgColor: 'bg-success/20' },
  wont_do: { label: "Won't Do", color: 'text-destructive', bgColor: 'bg-destructive/20' },
  duplicate: { label: 'Duplicate', color: 'text-muted-foreground', bgColor: 'bg-muted/60' },
}

export const urgencyConfig: Record<
  'low' | 'medium' | 'high',
  { label: string; color: string }
> = {
  low: { label: 'Low', color: 'text-success' },
  medium: { label: 'Medium', color: 'text-warning' },
  high: { label: 'High', color: 'text-destructive' },
}
